/**
 * PartnerServiceSelection
 *
 * UI réutilisable pour sélectionner les add-ons partenaires.
 * Utilisée :
 *   - À l'étape 3 du Register Partner (sélection initiale)
 *   - Sur /partner/billing (gestion des add-ons)
 *
 * Props :
 *   - selected : add-ons actuellement cochés
 *   - onChange : callback quand la sélection change
 *   - disabled : désactive les checkbox (pendant un appel API)
 *   - footerSlot : zone d'action sous le total (boutons)
 */
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Lock } from "lucide-react";
import {
  PARTNER_ADDON_SLUGS,
  PARTNER_ADDON_LABELS,
  PARTNER_BASE_AMOUNT_USD,
  PARTNER_ADDON_AMOUNT_USD,
  computePartnerMonthlyTotal,
  type PartnerAddonSlug,
} from "@/lib/partner-billing";

interface Props {
  selected: PartnerAddonSlug[];
  onChange: (next: PartnerAddonSlug[]) => void;
  disabled?: boolean;
  footerSlot?: React.ReactNode;
  showHeader?: boolean;
  baseTier?: 'basic' | 'pro' | 'elite';
}

export function PartnerServiceSelection({
  selected,
  onChange,
  disabled = false,
  footerSlot,
  showHeader = true,
  baseTier,
}: Props) {
  const toggle = (slug: PartnerAddonSlug, checked: boolean) => {
    if (disabled) return;
    if (checked) {
      if (!selected.includes(slug)) onChange([...selected, slug]);
    } else {
      onChange(selected.filter((s) => s !== slug));
    }
  };

  const basePlanPrice = (() => {
  if (baseTier) {
    const prices: Record<string, number> = { basic: 99, pro: 199, elite: 349 };
    return prices[baseTier] || 99;
  }
  try {
    const saved = localStorage.getItem('selectedPartnerPlan');
    if (saved) {
      const { plan } = JSON.parse(saved);
      const prices: Record<string, number> = { basic: 99, pro: 199, elite: 349 };
      return prices[plan] || 99;
    }
  } catch {}
  return 99;
})();
  const monthlyTotal = basePlanPrice + selected.length * PARTNER_ADDON_AMOUNT_USD;

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="space-y-1">
          <h3 className="text-lg font-semibold text-[#1D314D]">
            Service Selection
          </h3>
          <p className="text-sm text-slate-600">
            The Partner Base is required. Select any additional services you
            want to offer through AcquizGroup.
          </p>
        </div>
      )}

      {/* Base — verrouillée */}
      <Card className="border-2 border-[#CEAB65] bg-[#CEAB65]/10 p-4">
        <div className="flex items-start gap-3">
          <div className="mt-1 flex h-5 w-5 items-center justify-center rounded border-2 border-[#CEAB65] bg-[#CEAB65]">
            <Lock className="h-3 w-3 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <p className="font-semibold text-[#1D314D]">Partner Base</p>
              <p className="font-bold text-[#1D314D]">
                {(() => {
                  try {
                    const saved = localStorage.getItem('selectedPartnerPlan');
                    if (saved) {
                      const { plan } = JSON.parse(saved);
                      const names: Record<string, string> = { basic: 'Basic $99/mo', pro: 'Pro $199/mo', elite: 'Elite $349/mo' };
                      return names[plan] || `$${PARTNER_BASE_AMOUNT_USD}/mo`;
                    }
                  } catch {}
                  return `$${PARTNER_BASE_AMOUNT_USD}/mo`;
                })()}
              </p>
            </div>
            <p className="mt-1 text-xs text-slate-600">
              Required — gives you access to the Internal Partner platform.
            </p>
          </div>
        </div>
      </Card>

      {/* Add-ons */}
      <div className="space-y-2">
        {PARTNER_ADDON_SLUGS.map((slug) => {
          const checked = selected.includes(slug);
          return (
            <Card
              key={slug}
              className={`p-4 transition-colors ${
                checked
                  ? "border-[#1D314D] bg-slate-50"
                  : "border-slate-200 hover:border-slate-300"
              } ${disabled ? "opacity-60" : ""}`}
            >
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={(v) => toggle(slug, v === true)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="font-medium text-slate-900">
                      {PARTNER_ADDON_LABELS[slug]}
                    </p>
                    <p className="font-semibold text-slate-900">
                      +${PARTNER_ADDON_AMOUNT_USD}
                      <span className="text-xs font-normal text-slate-600">
                        /month
                      </span>
                    </p>
                  </div>
                </div>
              </label>
            </Card>
          );
        })}
      </div>

      {/* Total */}
      <Card className="border-2 border-[#1D314D] bg-[#1D314D] p-4 text-white">
        <div className="flex items-baseline justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-white/70">
              Monthly Total
            </p>
            <p className="text-xs text-white/60">
              Base ${basePlanPrice} + {selected.length} add-on
              {selected.length === 1 ? "" : "s"} × ${PARTNER_ADDON_AMOUNT_USD}
            </p>
          </div>
          <p className="text-3xl font-bold text-[#CEAB65]">
            ${monthlyTotal}
            <span className="text-sm font-normal text-white/70">/mo</span>
          </p>
        </div>
      </Card>

      {footerSlot}
    </div>
  );
}
